import { useState } from "react";
import {ActionFeedback,OutcomePanel,KV,joinNonEmpty} from "./ProspectReviewParts";
import { Badge, Btn } from "../../ui";
import {
  ApiRequestError,
  confirmProspectMatch,
  markProspectNoMatch,
  type Omonimo,
  type ProspectDetail,
  type ProspectMatchChoice,
} from "../../api";
import {
  formatDateTime,
  prospectOutcome,
} from "./ProspectListParts";
import { HerobotSyncPanel } from "./HerobotSyncPanel";

import {isHttpUrl,buildOmonimoRows} from "./prospect-review-utils";
import { matchChoiceFor, OmonimiList } from "./OmonimiList";

export function ProspectReviewPanel({
  detail,
  onUpdated,
}: {
  detail: ProspectDetail;
  onUpdated?: (next: ProspectDetail) => void;
}) {
  const outcome = prospectOutcome({
    ...detail,
    omonimiSituation: detail.omonimiSituation,
  });

  const [showAllOmonimi, setShowAllOmonimi] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);

  const showOmonimiActions =
    detail.status === "omonimi_found" &&
    detail.omonimi.length > 0 &&
    !detail.hasVisuraXml;

  // Tabella omonimi in ordine di score (i candidates sono già ordinati desc).
  const omonimoRows = buildOmonimoRows(detail.omonimi, detail.candidates);
  // Compatibili = confermabili e con data coerente, anche senza CF.
  const compatibleRows = omonimoRows.filter(({ cand, omonimo }) => cand && !cand.yearOff && cand.autoMatchEligible !== false && matchChoiceFor(omonimo) !== null);
  const visibleOmonimi = showAllOmonimi || compatibleRows.length === 0 ? omonimoRows : compatibleRows;
  // Candidato consigliato: il migliore, ma solo quando la situazione è netta.
  const topCandidate =
    detail.omonimiSituation === "clear_single" && detail.candidates.length > 0
      ? detail.candidates[0]
      : null;

  async function runAction<T extends ProspectDetail>(
    action: () => Promise<T>,
    success: (next: T) => string,
    failure: string,
  ) {
    setActionBusy(true);
    setActionErr(null);
    setActionMsg(null);
    try {
      const next = await action();
      setActionMsg(success(next));
      onUpdated?.(next);
    } catch (e) {
      setActionErr(e instanceof ApiRequestError ? e.message : failure);
    } finally {
      setActionBusy(false);
    }
  }

  const runConfirm = (choice: ProspectMatchChoice, omonimo: Omonimo) => {
    const who = "cf" in choice
      ? `Codice fiscale ${choice.cf}`
      : `${[omonimo.cognome, omonimo.nome].filter(Boolean).join(" ") || "Omonimo"} (senza codice fiscale)`;
    return runAction(
      () => confirmProspectMatch(detail.id, choice),
      // requeued=false: senza distinzione l'operatore aspettava una visura che
      // non sarebbe mai arrivata (task cancellato dalla pulizia coda o mai esistito).
      (next) => next.requeued
        ? `${who} confermato. La visura è di nuovo in coda sull'estensione.`
        : `${who} salvato, ma non c'è una ricerca da rilanciare: rimetti la persona in coda da Necrologi.`,
      "Conferma non riuscita",
    );
  };
  const runNoMatch = () => runAction(
    () => markProspectNoMatch(detail.id),
    () => "Segnato: nessun omonimo corrisponde alla persona.",
    "Operazione non riuscita",
  );

  return (
    <div className="col gap-16">
      <div className="row gap-6" style={{ flexWrap: "wrap" }}>
        {detail.discardedAt != null && <Badge kind="muted" title="Non compare nella lista di lavoro. Usa «Rimetti in lista» in alto.">scartato</Badge>}
        {detail.casaVuota && (
          <Badge kind="warn" title="Coniuge non citato nel necrologio: indicazione da verificare. Non identifica gli eredi né conferma che la casa sia vuota.">
            Coniuge non citato · Da verificare
          </Badge>
        )}
        {detail.vedovo && !detail.casaVuota && (
          <Badge kind="info" title="Necrologio con ved./vedovo/vedova rilevato">ved.</Badge>
        )}
        <Badge kind="muted">{detail.source}</Badge>
        {detail.provincia && <Badge kind="muted">{detail.provincia}</Badge>}
        {detail.hasVisuraXml && <Badge kind="muted">visura salvata</Badge>}
        {detail.matchedCf && (
          <Badge kind="info">CF {detail.matchedCf}</Badge>
        )}
        {detail.link && isHttpUrl(detail.link) && (
          <a
            href={detail.link}
            target="_blank"
            rel="noreferrer"
            className="chip mono"
            style={{ fontSize: 11, color: "var(--accent)" }}
          >
            apri necrologio ↗
          </a>
        )}
      </div>

      <OutcomePanel outcome={outcome}>
        <div className="row gap-6" style={{ flexWrap: "wrap", marginTop: 8 }}>
          {(detail.status === "visura_pdf" || detail.status === "omonimi_found") ? <Badge kind="muted">Dati catastali non ancora disponibili</Badge> : <>
          <Badge kind={detail.immobili.length > 0 ? "accent" : "muted"}>
            {detail.immobili.length} immobili in tabella
          </Badge>
          <Badge kind={detail.province.length > 0 ? "accent" : "muted"}>
            {detail.province.length} province
          </Badge></>}
          {detail.omonimi.length > 0 && <Badge kind="accent">{detail.omonimi.length} omonimi</Badge>}
        </div>
      </OutcomePanel>

      {detail.omonimi.length > 0 && (
        <details className="omonimi-section" open={detail.status === "omonimi_found"}>
          <summary>Omonimi trovati su SISTER ({detail.omonimi.length})</summary>
          <p className="omonimi-help">
            {detail.omonimiSituation === "incompatible_single"
              ? "Un solo candidato, ma anno o luogo non combaciano con il necrologio: probabilmente è un'altra persona."
              : "Confronta ogni omonimo con il necrologio. Se riconosci la persona, premi Conferma: l'estensione rifà la visura su quella persona, anche se SISTER non riporta il codice fiscale."}
          </p>
          {(showOmonimiActions || compatibleRows.length > 0 && compatibleRows.length < omonimoRows.length) && (
            <div className="omonimi-toolbar">
              {compatibleRows.length > 0 && compatibleRows.length < omonimoRows.length && <>
                <span>{compatibleRows.length} {compatibleRows.length === 1 ? "candidato compatibile" : "candidati compatibili"} su {omonimoRows.length} omonimi distinti</span>
                <Btn size="sm" onClick={() => setShowAllOmonimi(value => !value)}>{showAllOmonimi ? "Mostra solo i compatibili" : "Mostra tutti gli omonimi"}</Btn>
              </>}
              {showOmonimiActions && (
                <Btn size="sm" disabled={actionBusy} onClick={() => void runNoMatch()}>
                  Nessuno è la persona giusta
                </Btn>
              )}
            </div>
          )}
          <ActionFeedback msg={actionMsg} err={actionErr} />
          <OmonimiList
            rows={visibleOmonimi}
            matchedCf={detail.matchedCf}
            confirmedOmonimoId={detail.confirmedOmonimoId}
            topCandidate={topCandidate}
            canConfirm={showOmonimiActions}
            busy={actionBusy}
            onConfirm={(choice, omonimo) => void runConfirm(choice, omonimo)}
          />
        </details>
      )}

      <HerobotSyncPanel key={detail.id} detail={detail} onUpdated={onUpdated} />

      <div className="grid grid-2 gap-12">
        <KV k="Cercato" v={formatDateTime(detail.searchedAt)} />
        <KV k="Nome usato" v={joinNonEmpty([detail.cognomeUsed, detail.nomeUsed], " ")} />
        <KV k="Anno nascita usato" v={detail.annoUsed != null ? String(detail.annoUsed) : "—"} />
        <KV k="CF confermato" v={detail.matchedCf ?? (detail.confirmedOmonimoId ? "persona senza CF scelta" : "—")} />
        <KV k="Impresa funebre" v={detail.impresaFunebre ?? "—"} sub={detail.telefonoImpresa ?? undefined} />
        <KV k="Immobili / province" v={(detail.status === "visura_pdf" || detail.status === "omonimi_found") ? "Non ancora disponibili" : `${detail.immobili.length} / ${detail.province.length}`} />
      </div>

      {detail.error && (
        <div
          className="preview-box"
          style={{
            background: "var(--danger-dim)",
            borderColor: "var(--danger)",
            color: "var(--danger)",
            fontSize: 12,
          }}
        >
          <strong>Errore:</strong> {detail.error}
        </div>
      )}

      {detail.contenuto && (
        <details>
          <summary className="k" style={{ cursor: "pointer" }}>Testo necrologio</summary>
          <div
            className="preview-box"
            style={{ fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap", marginTop: 8 }}
          >
            {detail.contenuto}
          </div>
        </details>
      )}

      {detail.province.length > 0 && (
        <div className="col gap-6">
          <div className="k" style={{ fontWeight: 600 }}>Province immobili</div>
          <div className="row gap-6" style={{ flexWrap: "wrap" }}>
            {detail.province.map((p) => (
              <Badge key={p.provincia} kind="accent">
                {p.provincia} · {p.fabbricati}F / {p.terreni}T
              </Badge>
            ))}
          </div>
        </div>
      )}

      {detail.immobili.length === 0 && detail.status === "visura_ok" && (
        <div className="muted" style={{ fontSize: 12 }}>
          Visura completata senza immobili in tabella. Usa «Recupera dati visura» se il file salvato contiene dati.
        </div>
      )}

      <div className="muted" style={{ fontSize: 11 }}>
        Prospect #{detail.id} · necrologio #{detail.necrologioId}
        {detail.visuraIdRichiesta && ` · richiesta visura ${detail.visuraIdRichiesta}`}
        {detail.visuraDownloadedAt && ` · visura salvata ${formatDateTime(detail.visuraDownloadedAt)}`}
      </div>
    </div>
  );
}
